import { Backdrop, Box, CircularProgress, Grid, useTheme } from '@mui/material'
import { withObservables } from '@nozbe/watermelondb/react'
import { useTour } from '@reactour/tour'
import Analytics, { BlockedFlow, Events } from 'analytics/Analytics'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { supportedFeatures } from 'components/dialogs/payment/PlansInfoDialog'
import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import { useAuth } from 'components/providers/AuthProvider'
import { usePayment } from 'components/providers/PaymentProvider'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { ComponentType, useEffect, useState } from 'react'
import { downgradeGroupOnServer } from 'services/api/group'
import Database from 'services/db/Database'
import ObserveService from 'services/db/Observe'
import { Account } from 'services/db/Queries/AccountQueries'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { AccountDoc, GroupDoc } from 'types/api/changes'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  highestActiveProduct,
  highestActiveTier,
  Tier
} from 'utils/api/accounts'
import { TierFeatures } from 'utils/api/group_state'
import {
  outOfTiersFeaturesForGroup,
  provideNeededTierForGroup
} from 'utils/api/groups'
import { APP_COLOR } from 'utils/colors'
import { isConvertedGroup } from 'utils/groups'
import { outOfTiersFeaturesForAccount } from 'utils/tier'

import { humanReadableTier, tierToPlan } from './TierHover'

interface OutOfTierDialogProps {
  group?: GroupDoc
  setOpen: (b: boolean) => void
  opened: boolean
  account: AccountDoc
  flow?: BlockedFlow
  onClose?: () => void
}

export function OutOfTierDialog(props: OutOfTierDialogProps) {
  const theme = useTheme()
  const tour = useTour()
  const tfmTour = useTfmTourProvider()
  const activeTier = highestActiveTier(props.account)
  const auth = useAuth()
  const { showPaymentDialog } = usePayment()
  let landlineMode = false
  let neededTier = provideNeededTierForGroup(activeTier, props.group)
  const [loading, setLoading] = useState(0)

  const isUserOwnerOfGroup =
    props.group?.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)

  let outOfTierFeatures: TierFeatures[] = []

  if (props.group) {
    outOfTierFeatures = outOfTiersFeaturesForGroup(
      activeTier,
      props.group,
      props.account
    )
  } else if (props.account) {
    outOfTierFeatures = outOfTiersFeaturesForAccount(props.account)
  }

  /**
   * Only in case of out of tier feature is landline and it's the only one, we show special dialog
   * in that case
   */
  if (
    outOfTierFeatures.length === 1 &&
    outOfTierFeatures[0] === TierFeatures.LANDLINE
  ) {
    neededTier = Tier.BUSINESS
    landlineMode = true
  }

  const showPayments = () => {
    logAnalytics(Events.blockedGroupDialogUpgradeClick)

    showPaymentDialog(
      tierToPlan(neededTier),
      (status) => {
        Account().then((acc) => {
          if (
            acc &&
            highestActiveTier(acc) >= neededTier &&
            status === 'DIALOG_CLOSED'
          ) {
            props.setOpen(false)

            if (landlineMode) {
              LocalStorage.set(LocalStorageKey.LANDLINE_DIALOG, '0')
            }
          }
        })
      },
      tierFeaturesToAnalyticsFeatures(outOfTierFeatures)
    )
  }

  const hideOutOfTier = () => {
    props.setOpen(false)

    if (props.onClose) {
      props.onClose()
    }

    logAnalytics(Events.blockedGroupDialogCancelClick)

    if (landlineMode) {
      LocalStorage.set(LocalStorageKey.LANDLINE_DIALOG, '0')

      setTimeout(() => {
        Logger.blue('Header:Opening Tour', tour.steps)
        tfmTour.setMode(ONBOARDING_MODE.FIRST_LOGIN)

        if (tour.setSteps && tfmTour.getStepsByMode) {
          tour.setSteps(tfmTour.getStepsByMode(ONBOARDING_MODE.FIRST_LOGIN))
        }
        tour.setCurrentStep(0)
        tour.setIsOpen(true)
      }, 250)
    }
  }

  const updatePhoneNumber = () => {
    // move user to login
    auth.onPhoneTypeUpdateFlow(true)

    LocalStorage.set(LocalStorageKey.LANDLINE_DIALOG, '0')
  }

  const downgradeGroup = async () => {
    setLoading(1)
    if (props.group) {
      await downgradeGroupOnServer(props.group._id ?? '').then(() => {
        if (isConvertedGroup(props.group)) {
          return Database.write(async () => {
            ;(await localGroupById(props.group?._id ?? '')).update((local) => {
              local.PrivateLinkPassword = ''
              local.PrivateLinkUrl = ''
              local._raw['_sortOrder'] = dayjs().unix()
            })
          })
        }

        return Promise.resolve()
      })
      logAnalytics(Events.blockedGroupDialogDowngradeClick)
    }
    setLoading(0)
    props.setOpen(false)
  }

  if (props.opened) highestActiveProduct(props.account)

  function logAnalytics(event: Events) {
    Analytics.getInstance().sendOutOfTierDialogAnalytics(
      event,
      isUserOwnerOfGroup ? 'Yes' : 'No',
      tierToAnalyticsTier(activeTier),
      tierFeaturesToAnalyticsFeatures(outOfTierFeatures),
      tierToAnalyticsTier(neededTier),
      props.flow
    )
  }

  useEffect(() => {
    if (props.opened) {
      logAnalytics(Events.blockedDialogPresented)

      if (landlineMode) {
        LocalStorage.set(LocalStorageKey.LANDLINE_DIALOG, '1')
      }
    } else {
      if (landlineMode) {
        LocalStorage.set(LocalStorageKey.LANDLINE_DIALOG, '0')
      }
    }
  }, [props.opened])

  return (
    <ConfirmationDialog
      title={t('upgradeTo', { tier: humanReadableTier(neededTier) })}
      content={
        <>
          <Grid container flexDirection={'column'} rowGap={4}>
            {landlineMode ? <></> : <div>{t('thisSharedCalendarUses')}</div>}
            {landlineMode ? (
              <div>{t('landlineError', { appName: 'GroupCal' })}</div>
            ) : (
              <Box marginLeft={4}>
                {outOfTierFeatures.map((featureCode) => {
                  const feature = supportedFeatures.find(
                    (supported) => supported.key === featureCode
                  )
                  return (
                    <div
                      key={featureCode}
                    >{`- ${feature?.outOfTierExplanation ?? feature?.title}`}</div>
                  )
                })}
              </Box>
            )}
            {landlineMode ? <></> : <Box>{t('youCanEither')}</Box>}
          </Grid>
          <Backdrop
            sx={{ color: APP_COLOR, zIndex: 99999 }}
            open={loading === 1}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </>
      }
      leftButton={
        landlineMode ? `${t('changeToMobileNumber')}` : `${t('upgrade')}`
      }
      rightButton={
        landlineMode ? `${t('upgrade')}` : `${t('downgradeCalendar')}`
      }
      rightButtonColor={theme.palette.primary.main}
      leftButtonColor={'black'}
      handleClose={hideOutOfTier}
      handleLeftButton={landlineMode ? updatePhoneNumber : showPayments}
      handleRightButton={landlineMode ? showPayments : downgradeGroup}
      open={props.opened}
    />
  )
}

export default withObservables([], () => ({
  account: ObserveService.Account()
}))(OutOfTierDialog as ComponentType<unknown>) as any

function tierFeaturesToAnalyticsFeatures(
  outOfTierFeatures: TierFeatures[]
): string[] {
  return outOfTierFeatures.map((feature) => {
    return tierFeaturesToAnalyticsFeature(feature)
  })
}

export function tierFeaturesToAnalyticsFeature(feature?: TierFeatures): string {
  switch (feature) {
    case TierFeatures.ADMINS:
      return 'NumOfAdmins'
    case TierFeatures.BUSINESSINFO:
      return 'BusinessPage'
    case TierFeatures.CHANNEL:
      return 'CalendarChannel'
    case TierFeatures.LANDLINE:
      return 'LandlineNumber'
    case TierFeatures.MEMBERCOLOR:
      return 'MembersColors'
    case TierFeatures.PERMISSIONS:
      return 'PermissionsManagement'
    case TierFeatures.PERMISSIONS_CALENDAR_NAME:
      return 'CalendarName'
    case TierFeatures.PERMISSIONS_EVENTS:
      return 'PermissionsEvents'
    case TierFeatures.PERMISSIONS_MEMBERS:
      return 'PermissionsMembers'
    case TierFeatures.PRIORITY_SUPPORT:
      return 'PrioritySupport'
    case TierFeatures.RSVP:
      return 'RSVP'
    case TierFeatures.VERIFIED:
      return 'VerifiedCalendar'
    case TierFeatures.ASSIGN_EVENT_OWNERSHIP:
      return 'AssignEventOwnership'
    case TierFeatures.EMBED_CALENDARS:
      return 'EmbedCalendars'
    case TierFeatures.SYNC_WITH_CALENDARS:
      return 'SyncWithCalendars'
    case TierFeatures.EDIT_THEIR_EVENTS_ONLY:
      return 'EditTheirEventsOnly'
    case TierFeatures.REMOVE_BRANDING:
      return 'RemoveBranding'
    case TierFeatures.SEE_EVENTS_DETAILS:
      return 'SeeEventsDetails'
    case TierFeatures.SHARE_PERSONAL_CALENDAR:
      return 'SharePersonalCalendar'
    case TierFeatures.COMMUNITY_VIEW:
      return 'CommunityView'
    case TierFeatures.EVENT_PHOTO_ATTACHMENTS:
      return 'EventPhotoAttachments'
    case TierFeatures.LINK_CALENDARS:
      return 'LinkCalendars'
  }

  return ''
}

export function isTierFeature(key: string): boolean {
  return Object.values(TierFeatures).includes(key as TierFeatures)
}

export function analyticsFeatureToTierFeature(
  feature: string
): TierFeatures | undefined {
  switch (feature) {
    case 'NumOfAdmins':
      return TierFeatures.ADMINS
    case 'BusinessPage':
      return TierFeatures.BUSINESSINFO
    case 'CalendarChannel':
      return TierFeatures.CHANNEL
    case 'LandlineNumber':
      return TierFeatures.LANDLINE
    case 'MembersColors':
      return TierFeatures.MEMBERCOLOR
    case 'PermissionsManagement':
      return TierFeatures.PERMISSIONS
    case 'CalendarName':
      return TierFeatures.PERMISSIONS_CALENDAR_NAME
    case 'PermissionsEvents':
      return TierFeatures.PERMISSIONS_EVENTS
    case 'PermissionsMembers':
      return TierFeatures.PERMISSIONS_MEMBERS
    case 'PrioritySupport':
      return TierFeatures.PRIORITY_SUPPORT
    case 'RSVP':
      return TierFeatures.RSVP
    case 'VerifiedCalendar':
      return TierFeatures.VERIFIED
    case 'AssignEventOwnership':
      return TierFeatures.ASSIGN_EVENT_OWNERSHIP
    case 'EmbedCalendars':
      return TierFeatures.EMBED_CALENDARS
    case 'SyncWithCalendars':
      return TierFeatures.SYNC_WITH_CALENDARS
    case 'EditTheirEventsOnly':
      return TierFeatures.EDIT_THEIR_EVENTS_ONLY
    case 'RemoveBranding':
      return TierFeatures.REMOVE_BRANDING
    case 'SeeEventsDetails':
      return TierFeatures.SEE_EVENTS_DETAILS
    case 'SharePersonalCalendar':
      return TierFeatures.SHARE_PERSONAL_CALENDAR
    case 'CommunityView':
      return TierFeatures.COMMUNITY_VIEW
    case 'EventPhotoAttachments':
      return TierFeatures.EVENT_PHOTO_ATTACHMENTS
    case 'LinkCalendars':
      return TierFeatures.LINK_CALENDARS
  }

  return undefined
}

export function tierToAnalyticsTier(tier: Tier) {
  switch (tier) {
    case Tier.PRO:
      return 'Tier10'
    case Tier.BUSINESS:
      return 'Tier20'
    case Tier.BUSINESS_PLUS:
      return 'Tier30'
    default:
      return 'Free'
  }
}
